import { ScheduleEntry, ScheduleResult, UpsertScheduleBreakDto } from "@myapp/contract";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { scheduleKeys } from "../schedule.keys";
import { scheduleApi } from "../service";

function isSameDay(isoA: string, isoB: string): boolean {
  const a = new Date(isoA);
  const b = new Date(isoB);
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Move (or create) a technician's break for one day.
 *
 * The optimistic patch rewrites the BREAK entry inside the cached `ScheduleResult`. Because the
 * response carries `breakDurationMinutes` per technician, the placeholder block is now drawn at
 * the real length instead of a hardcoded 30 minutes that snapped on settle.
 */
export function useUpsertBreak() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (dto: UpsertScheduleBreakDto) => {
      const { data } = await scheduleApi.upsertBreak(dto);
      return data;
    },

    onMutate: async (dto) => {
      await queryClient.cancelQueries({ queryKey: scheduleKeys.ranges() });

      const previousQueries = queryClient.getQueriesData<ScheduleResult>({
        queryKey: scheduleKeys.ranges(),
      });

      queryClient.setQueriesData<ScheduleResult>(
        { queryKey: scheduleKeys.ranges() },
        (old) => {
          if (!old) return old;

          return {
            ...old,
            technicians: old.technicians.map((tech) => {
              if (tech.technicianId !== dto.technicianId) return tech;

              const endsAt = new Date(
                new Date(dto.startsAt).getTime() + tech.breakDurationMinutes * 60_000,
              ).toISOString();

              const existingIdx = tech.entries.findIndex(
                (e) => e.type === "BREAK" && isSameDay(e.startsAt, dto.startsAt),
              );

              // A projected break (id: null) is replaced in place — dragging one is exactly the
              // act of materializing it, so there must not be two blocks on that day.
              const entries =
                existingIdx >= 0
                  ? tech.entries.map((entry, i) =>
                      i !== existingIdx
                        ? entry
                        : {
                            ...entry,
                            startsAt: dto.startsAt,
                            endsAt,
                            plannedWorkStartsAt: dto.startsAt,
                            plannedWorkMinutes: tech.breakDurationMinutes,
                          },
                    )
                  : [
                      ...tech.entries,
                      optimisticBreak(
                        dto.technicianId,
                        dto.startsAt,
                        endsAt,
                        tech.breakDurationMinutes,
                      ),
                    ];

              return {
                ...tech,
                entries: [...entries].sort((a, b) => a.startsAt.localeCompare(b.startsAt)),
              };
            }),
          };
        },
      );

      return { previousQueries };
    },

    onError: (_err, _vars, context) => {
      if (context?.previousQueries) {
        for (const [queryKey, data] of context.previousQueries) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
    },
  });
}

/** id -1 marks a row that only exists until the mutation settles. */
function optimisticBreak(
  technicianId: number,
  startsAt: string,
  endsAt: string,
  durationMinutes: number,
): ScheduleEntry {
  return {
    id: -1,
    type: "BREAK",
    technicianId,
    createdByUserId: null,
    startsAt,
    endsAt,
    plannedWorkStartsAt: startsAt,
    plannedTravelMinutes: 0,
    plannedWorkMinutes: durationMinutes,
    plannedDepartureMinutes: 0,
    actualWorkStartsAt: null,
    actualEndsAt: null,
    travelStartedAt: null,
    departureEndedAt: null,
    actualTravelMinutes: null,
    actualWorkMinutes: null,
    actualDepartureMinutes: null,
    allDay: false,
    needsRescheduling: false,
    reschedulingCause: null,
    reschedulingReason: null,
    reschedulingRequestedAt: null,
    appointmentId: null,
    taskId: null,
    absenceId: null,
    createdAt: null,
    updatedAt: null,
  };
}
